import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import '../styles/Sidebar.css';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Direct navigation handler
  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleNavLinkClick = (path) => {
    // Close the sidebar on mobile
    if (isMobile && onClose) {
      onClose();
    }
  };

  // Get active class
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="sidebar-overlay visible" 
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        {/* Close button (mobile only) */}
        <button
          className="close-sidebar"
          onClick={onClose}
        >
          ✖
        </button>
        
        {/* Sidebar header - removed logo and app name */}
        <div className="sidebar-header">
          {/* Empty header to maintain spacing */}
        </div>
        
        {/* Navigation */}
        <nav className="nav-menu">
          <Link
            to="/dashboard"
            className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}
            onClick={() => handleNavLinkClick('/dashboard')}
          >
            Dashboard
          </Link>
          {user && user.role === 'student' && (
            <>
              <Link
                to="/assignments/student"
                className={`nav-item ${isActive('/assignments/student') ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('/assignments/student')}
              >
                Assignments
              </Link>
              <Link
                to="/progress"
                className={`nav-item ${isActive('/progress') ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('/progress')}
              >
                Progress
              </Link>
            </>
          )}
          {/* Desktop view for teacher role - only show specific nav items */}
          {user && user.role === 'teacher' && !isMobile && (
            <Link
              to="/attendance"
              className={`nav-item ${isActive('/attendance') ? 'active' : ''}`}
              onClick={() => handleNavLinkClick('/attendance')}
            >
              Attendance
            </Link>
          )}
          {/* Mobile view for teacher role - show all nav items */}
          {user && user.role === 'teacher' && isMobile && (
            <>
              <Link
                to="/classes/students"
                className={`nav-item ${isActive('/classes/students') ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('/classes/students')}
              >
                Students
              </Link>
              <Link
                to="/attendance"
                className={`nav-item ${isActive('/attendance') ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('/attendance')}
              >
                Attendance
              </Link>
            </>
          )}
          {user && user.role === 'parent' && isMobile && (
            <>
              <Link
                to="/children"
                className={`nav-item ${isActive('/children') ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('/children')}
              >
                Children
              </Link>
              <Link
                to="/progress"
                className={`nav-item ${isActive('/progress') ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('/progress')}
              >
                Progress
              </Link>
            </>
          )}
          {user && (user.role === 'admin' || user.role === 'super_admin') && (
            <>
              <Link
                to="/admin/attendance"
                className={`nav-item ${isActive('/admin/attendance') ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('/admin/attendance')}
              >
                Attendance
              </Link>
              <Link
                to="/users"
                className={`nav-item ${isActive('/users') ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('/users')}
              >
                Users
              </Link>
              <Link
                to="/classes"
                className={`nav-item ${isActive('/classes') ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('/classes')}
              >
                Classes
              </Link>
              <Link
                to="/reports"
                className={`nav-item ${isActive('/reports') ? 'active' : ''}`}
                onClick={() => handleNavLinkClick('/reports')}
              >
                Reports
              </Link>
            </>
          )}
          {/* Show Messages link for all users in all views */}
          <Link
            to="/messages"
            className={`nav-item ${isActive('/messages') ? 'active' : ''}`}
            onClick={() => handleNavLinkClick('/messages')}
          >
            Messages
          </Link>
          {/* Show Settings link for all users in all views */}
          <Link
            to="/settings"
            className={`nav-item ${isActive('/settings') ? 'active' : ''}`}
            onClick={() => handleNavLinkClick('/settings')}
          >
            Settings
          </Link>
        </nav>
        
        {/* User Info */}
        <div className="user-info">
          <div className="user-avatar">👤</div>
          <div className="user-details">
            <p className="user-name">{user?.name || 'User'}</p>
            <p className="user-role">{user?.role || 'Guest'}</p>
          </div>
          <button
            className="logout-btn"
            onClick={() => {
              if (window.confirm('Are you sure you want to log out?')) {
                logout();
              }
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;